import { useUserStore } from '@/store/userStore';
import { useFeatureStore } from '@/store/featureStore';
import { serverUrls } from './constants';
import router from '@/router';
import { notify } from '@/utils/toaster';
import i18n from '@/utils/i18n';

const doFetch = async (method, path, body, keycloak) => {
  const res = await fetch(encodeURI(`${serverUrls.authUrl}${path}`), {
    method: method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + keycloak.token,
    },
    body: body ? JSON.stringify(body) : null,
  });
  const data = await res.json();
  if (!res.ok && !data.statusCode) {
    data.statusCode = res.status;
  }
  return data;
};

const retryAfterTokenRefresh = async (method, path, body, keycloak, userStore) => {
  try {
    await keycloak.updateToken(-1);
    return await doFetch(method, path, body, keycloak);
  } catch {
    userStore.silentReAuth();
    return null;
  }
};

const recoverForbiddenResponse = async (res, method, path, body, keycloak, userStore) => {
  const featureStore = useFeatureStore();
  await featureStore.refresh();
  if (featureStore.isEnabled('maintenance')) {
    router.push('/maintenance');
    return res;
  }
  return retryAfterTokenRefresh(method, path, body, keycloak, userStore);
};

const handleResponse = (res, showErrorToast, onError) => {
  if (res.statusCode >= 400) onError?.(res);
  if (res.statusCode === 409) {
    if (showErrorToast) {
      const names = res.homonyms?.map((h) => (h.job ? `${h.fullName} (${h.job})` : h.fullName)).join(', ');
      notify.error(i18n.global.t('add-people.homonym-title'), {
        description: i18n.global.t('add-people.homonym-description', { names: names ?? res.message }),
      });
    }
    return;
  }

  if ((res.statusCode >= 200 && res.statusCode < 300) || !res.statusCode) {
    return res;
  }
  if (showErrorToast) {
    notify.error('API Error', { description: `Something went wrong. Error code: ${res.statusCode}` });
  }
};

export const callAPI = async (method, path, body, { showErrorToast = true, onError } = {}) => {
  const userStore = useUserStore();
  const keycloak = userStore.keycloak;

  if (!keycloak) {
    console.error('Keycloak non initialisé');
    return;
  }

  try {
    // Rafraîchir le token s'il expire dans les 30 prochaines secondes
    await keycloak.updateToken(30);
  } catch {
    // Le refresh a échoué, on tente quand même l'appel avec le token actuel
  }

  try {
    let res = await doFetch(method, path, body, keycloak);

    if (res.statusCode === 401) {
      // Token invalide : tenter un refresh puis réessayer une fois
      res = await retryAfterTokenRefresh(method, path, body, keycloak, userStore);
      if (!res) return;
    }

    if (res.statusCode === 403) {
      // Vérifier si c'est un vrai mode maintenance (feature flag Unleash) ou un problème d'auth
      res = await recoverForbiddenResponse(res, method, path, body, keycloak, userStore);
      if (!res) return;
    }

    return handleResponse(res, showErrorToast, onError);
  } catch (err) {
    console.error(err);
    onError?.(err);
    if (showErrorToast) {
      notify.error('Erreur API', {
        description: err.message || 'Une erreur est survenue lors de la communication avec le serveur.',
      });
    }
  }
};
